import type { ReviewCoverageReadView, ReviewCoverageView } from '../api.js';
import { timestamp } from '../format.js';
import { protectedCategoryLabel } from '../register-finding.js';

/**
 * Which protected questions the technical review ASKED on this project (#1379).
 *
 * 🔴 WHY THIS IS ON THE PROJECT. A technical-review pass refused for size (or failed at the gateway, or
 * answered unreadably, or over material the seam could not serve) answers nothing — and a size refusal
 * repeats on every run. Before this, only a worker log said so: the project showed no life-safety
 * candidate, which reads as a clean result, while its life-safety question had never been answered.
 * Brad ruled no pager alarm; the trace goes here, beside the candidates.
 *
 * - `not_run` is LOUD, and dated by `since` — how long the question has gone unasked on every run
 *   recorded, kept across a change of reason.
 * - `not_asked` is QUIET: nothing of its kind is on the record. Routine, but still no answer.
 * - `ran` says since when, by `since` like the loud line, and claims nothing more: asked is not the
 *   same as clean.
 * - A row the Hub could not read is SAID, never dropped.
 * - The DESIGN review adds one run-level row: NOT ANSWERED, loud, when its latest run did not land
 *   (superseded, or no pass ran); `ran`, quiet, when it did.
 *
 * `null` or nothing renders nothing: the server did not say, which is not "every question was asked".
 */
const ORDER: Record<ReviewCoverageView['outcome'], number> = { not_run: 0, not_asked: 1, ran: 2 };

/** A technical pass's category, as a person reads it. (The design row has none, and its own render.) */
function labelOf(c: ReviewCoverageView): string {
  const category = c.category ?? '';
  return protectedCategoryLabel(category) ?? category;
}

/**
 * The design review's ONE run-level row (`design-review:run`): did the latest run LAND? A superseded
 * run (its documents or drawing record changed under it) or one where no pass ran wrote no finding —
 * and the findings shown are then an earlier run's. LOUD, like an unanswered protected question.
 */
function DesignRow({ c }: { c: ReviewCoverageView }) {
  if (c.outcome === 'ran') {
    return (
      <li className="coverage__row muted">
        Design review: latest run landed — on every run recorded since {timestamp(c.since)}. Passes
        it could not run are in its log; landed is not clean.
      </li>
    );
  }
  return (
    <li className="coverage__row coverage__row--gap" role="note">
      <strong>Design review: NOT ANSWERED</strong>
      {c.reason ? ` — ${c.reason}` : ''} (since {timestamp(c.since)}). No new finding here is not a
      clean result.
    </li>
  );
}

export function ReviewCoverage({ coverage }: { coverage: ReviewCoverageReadView | null }) {
  if (!coverage || (coverage.rows.length === 0 && coverage.unreadable === 0)) return null;
  const rows = [...coverage.rows].sort((a, b) => ORDER[a.outcome] - ORDER[b.outcome]);
  return (
    <ul className="coverage" aria-label="Review coverage">
      {coverage.unreadable > 0 && (
        <li className="coverage__row coverage__row--gap" role="note">
          {coverage.unreadable === 1
            ? 'A review coverage record could not be read.'
            : `${String(coverage.unreadable)} review coverage records could not be read.`}
        </li>
      )}
      {rows.map((c) => {
        if (c.review === 'design') return <DesignRow key={c.key} c={c} />;
        const label = labelOf(c);
        if (c.outcome === 'not_run') {
          return (
            <li key={c.key} className="coverage__row coverage__row--gap" role="note">
              <strong>{label}: NOT ANSWERED by the technical review</strong> — on every run recorded
              since {timestamp(c.since)}
              {c.reason ? ` (${c.reason})` : ''}. No {label.toLowerCase()} candidate here is not a
              clean result.
            </li>
          );
        }
        if (c.outcome === 'not_asked') {
          return (
            <li key={c.key} className="coverage__row muted">
              {label}: not asked{c.reason ? ` — ${c.reason}` : ''} (recorded{' '}
              {timestamp(c.recordedAt)}).
            </li>
          );
        }
        return (
          <li key={c.key} className="coverage__row muted">
            {label}: asked on every run recorded since {timestamp(c.since)}.
          </li>
        );
      })}
    </ul>
  );
}
